import { useEffect, useRef } from 'react';

interface LoadOnScrollProps {
  /** False while a page is loading or none is left, so one sighting asks for one page. */
  active: boolean;
  onLoad: () => void;
}

/**
 * Put at the end of a list: once scrolled into sight it asks for the next page. The observer
 * sees clipping by scrolling parents too, so it works inside a scrolling list as on the page.
 */
const LoadOnScroll = ({ active, onLoad }: LoadOnScrollProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const load = useRef(onLoad);
  load.current = onLoad;

  useEffect(() => {
    const element = ref.current;
    if (!active || !element || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) load.current();
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [active]);

  return <div ref={ref} data-testid="load-on-scroll" style={{ height: 1 }} />;
};

export default LoadOnScroll;
